// Sugalarm — cycle 468: the OWNER's lockout screen (phone + web).
//
// Shown in place of every route except the ones a locked person must keep
// (`lockoutCoversPath`: Settings, sign-in and recovery, pairing, admin) once
// the server says THIS account is locked (`service_state: "locked"` on
// `GET /api/me/subscription` — the stamp, never a clock). It renders the
// operator's sentence verbatim and the reactivation actions the existing
// rail matrix allows on this surface (`allowedRails`): the iPhone shows
// Apple plus the website link where allowed; Android shows Google Play plus
// the website link where allowed; the web shows the website. Restore
// purchases on a native surface that can. Settings → Subscription, sign out,
// delete account, Terms and Privacy stay reachable from here, so a locked
// person is never trapped.
//
// In flow inside the frame (no portal, no overlay, no z-index): it is what
// the frame shows instead of the route, so it can cover nothing. It is also
// the one place the No Data banner does not show (operator-authorized
// exception, cycle 468): a locked member's quiet feed is the lock, not an
// outage, and `useAlarmSounds` stands the No Data alarm down for it. It
// carries no audio and no alarm, so the alarm-control rules do not apply.
//
// A purchase here is the same server-confirmed flow as the Settings card:
// the client never flips its own state; it polls until the server's answer
// changes, and the lock clears in the grant's own transaction server-side.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { mintWebHandoff, type Subscription } from "../../api/subscription";
import {
  allowedRails,
  billingFailureMessage,
  discardExternalTab,
  getBillingSupport,
  getOffer,
  getStorefront,
  isAllowedBillingUrl,
  openExternal,
  prepareExternalTab,
  PRIVACY_URL,
  purchase,
  restore,
  subscriptionGrants,
  TERMS_URL,
  withBillingTimeout,
  type NativeOffer,
} from "../../lib/billing";
import { LAPSED_SENTENCE } from "../../lib/serviceLock";
import {
  applyServerSubscription,
  confirmWithServer,
  currentSubscriptionGeneration,
  refetchSubscription,
  useSubscription,
} from "../../hooks/useSubscription";
import { webLinkLabel } from "../onboarding/SubscriptionStep";
import styles from "./LockoutScreen.module.css";

type Phase = "idle" | "working" | "confirming";

export interface LockoutScreenProps {
  accountId: string | null;
  /** The shell's sign-out (the Settings "Log Out" row's action). */
  onSignOut: () => Promise<void> | void;
}

export function LockoutScreen({ accountId, onSignOut }: LockoutScreenProps) {
  const nav = useNavigate();
  const { data } = useSubscription();
  const support = getBillingSupport();
  const [storefront, setStorefront] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [note, setNote] = useState<string | null>(null);
  const [offer, setOffer] = useState<NativeOffer | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getStorefront().then((s) => {
      if (!cancelled) setStorefront(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const rails = allowedRails(support.platform, storefront);
  const native = support.platform === "ios" || support.platform === "android";
  const canSellNatively = native && support.supported;
  const trialEligible = data?.trial_eligible ?? false;
  const priceDisplay = data?.price_display ?? "";
  useEffect(() => {
    if (!canSellNatively || data == null) return;
    let cancelled = false;
    void getOffer({ trialEligible, priceDisplay }).then((o) => {
      if (!cancelled) setOffer(o);
    });
    return () => {
      cancelled = true;
    };
  }, [canSellNatively, data == null, trialEligible, priceDisplay]);

  const confirm = useCallback(
    async (accepted: Subscription | undefined, startedIn: number) => {
      if (accepted && subscriptionGrants(accepted)) {
        applyServerSubscription(accepted, startedIn);
        void refetchSubscription();
        setPhase("idle");
        return;
      }
      setPhase("confirming");
      const ok = await confirmWithServer(data);
      setPhase("idle");
      if (currentSubscriptionGeneration() !== startedIn) return;
      if (!ok) setNote("We couldn't confirm it yet. If you were charged, monitoring resumes shortly.");
    },
    [data],
  );

  const onReactivate = useCallback(async () => {
    setNote(null);
    if (support.platform === "web") {
      const r = await purchase({ productId: "sugaralarm_monthly", accountToken: accountId ?? "" });
      if (!r.ok) setNote("Couldn't open thesugaralarm.com. Try again.");
      return;
    }
    setPhase("working");
    const startedIn = currentSubscriptionGeneration();
    const current =
      offer ?? (data ? await getOffer({ trialEligible: data.trial_eligible, priceDisplay: data.price_display }) : null);
    if (current == null) {
      setPhase("idle");
      setNote(billingFailureMessage(support.supported ? "unavailable" : "unsupported", "purchase"));
      return;
    }
    const r = await purchase({
      productId: current.productId,
      accountToken: accountId ?? "",
      appAccountToken: current.appAccountToken,
      basePlanId: current.basePlanId,
      offerId: current.offerId,
    });
    if (!r.ok) {
      setPhase("idle");
      setNote(billingFailureMessage(r.reason, "purchase"));
      return;
    }
    await confirm(r.subscription, startedIn);
  }, [accountId, confirm, data, offer, support.platform, support.supported]);

  const onRestore = useCallback(async () => {
    setNote(null);
    setPhase("working");
    const startedIn = currentSubscriptionGeneration();
    const r = await restore();
    if (!r.ok) {
      setPhase("idle");
      setNote(billingFailureMessage(r.reason, "restore"));
      return;
    }
    await confirm(r.subscription, startedIn);
  }, [confirm]);

  const onWebLink = useCallback(async () => {
    setNote(null);
    const tab = prepareExternalTab();
    setPhase("working");
    const r = await withBillingTimeout(mintWebHandoff());
    setPhase("idle");
    if (r == null || !r.ok || !isAllowedBillingUrl(r.data.url)) {
      discardExternalTab(tab);
      setNote("Couldn't open thesugaralarm.com. Try again.");
      return;
    }
    if (!openExternal(r.data.url, tab).ok) setNote("Couldn't open thesugaralarm.com. Try again.");
  }, []);

  const busy = phase !== "idle";
  const showReactivate = rails.store != null ? support.supported : support.platform === "web";
  const linkLabel = rails.webLink ? webLinkLabel(support.platform) : null;

  return (
    <section className={styles.root} data-testid="lockout-screen" aria-labelledby="lockout-title">
      <div className={styles.card}>
        <h1 id="lockout-title" className={styles.title}>
          Monitoring has stopped
        </h1>
        <p className={styles.sentence} data-testid="lockout-sentence">
          {LAPSED_SENTENCE}
        </p>
        {phase === "confirming" ? (
          <p className={styles.line} role="status" aria-live="polite">
            Confirming with the store...
          </p>
        ) : null}
        {note ? (
          <p className={styles.line} role="alert">
            {note}
          </p>
        ) : null}
        <div className={styles.actions}>
          {showReactivate ? (
            <button
              type="button"
              className={styles.primary}
              data-billing-control="reactivate"
              disabled={busy}
              onClick={() => void onReactivate()}
            >
              Reactivate subscription
            </button>
          ) : null}
          {native && support.supported ? (
            <button
              type="button"
              className={styles.secondary}
              data-billing-control="restore"
              disabled={busy}
              onClick={() => void onRestore()}
            >
              Restore purchases
            </button>
          ) : null}
          {linkLabel ? (
            <button
              type="button"
              className={styles.secondary}
              data-billing-control="web-link"
              disabled={busy}
              onClick={() => void onWebLink()}
            >
              {linkLabel}
            </button>
          ) : null}
          <button
            type="button"
            className={styles.secondary}
            data-lockout-action="subscription-settings"
            onClick={() => nav("/settings?section=subscription")}
          >
            Subscription settings
          </button>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.textLink} data-lockout-action="sign-out" onClick={() => void onSignOut()}>
            Sign out
          </button>
          <button
            type="button"
            className={styles.textLink}
            data-lockout-action="delete-account"
            onClick={() => nav("/settings?section=account")}
          >
            Delete account
          </button>
          <button type="button" className={styles.textLink} data-lockout-action="terms" onClick={() => openExternal(TERMS_URL)}>
            Terms
          </button>
          <button type="button" className={styles.textLink} data-lockout-action="privacy" onClick={() => openExternal(PRIVACY_URL)}>
            Privacy
          </button>
        </div>
      </div>
    </section>
  );
}
